import { z } from 'zod';

import { type PdfPage, pdfPageSchema } from './signatureCoordinates';

/**
 * How a signer signs. Values are SignCare's `SignType` enum, sent as a STRING —
 * that is what `TemplateUsers.signType` carries on the wire.
 */
export const SIGN_TYPES = [
  { value: '1', label: 'Electronic', hint: 'Draw or type a signature in the browser' },
  { value: '2', label: 'Aadhaar', hint: 'Aadhaar eSign with OTP' },
  { value: '3', label: 'Digital (DSC)', hint: 'Signed with a digital signature certificate' },
] as const;

export const DEFAULT_SIGN_TYPE = '1';

/**
 * One signer on the template. Mirrors `TemplateUsers` on the server:
 * `{ orderNo, userId, Name, signType, pDFPages }` — note the capital `Name` and the
 * lowercase-p `pDFPages`, both of which the API is case-sensitive about.
 */
export const templateSignerSchema = z.object({
  orderNo: z.number().int(),
  userId: z.string(),
  Name: z.string(),
  signType: z.string(),
  pDFPages: z.array(pdfPageSchema).default([]),

  // ── Studio-only, never sent to SignCare ──────────────────────────────────
  // Who the signer IS, which decides where their email comes from at run time.
  // SignCare's template has no field for this: it stores the signature positions,
  // while the identity belongs to the workflow step that uses the template.
  /** `applicant` = whoever runs the session; `fixed` = the same person every run. */
  source: z.enum(['applicant', 'fixed']).optional(),
  /**
   * How a `fixed` signer is reached. SignCare takes either — an email signer gets an
   * emailed invite and an emailed OTP, a mobile signer gets both by SMS.
   */
  channel: z.enum(['email', 'mobile']).optional(),
  /** Only for a `fixed` signer — the applicant's address comes from the session. */
  email: z.string().optional(),
  /** Only for a `fixed` signer on the mobile channel. Bare 10-digit number. */
  mobile: z.string().optional(),
});

export type TemplateSigner = z.infer<typeof templateSignerSchema>;

/**
 * Colours used to tell one signer's boxes from another's on the page.
 *
 * Deliberately literal rather than design tokens: these are data-series colours
 * assigned by index, the same role a chart palette plays, and they have to stay
 * distinguishable from each other rather than match the UI's brand ramp.
 */
export const SIGNER_COLORS = [
  '#0048ff',
  '#00a37a',
  '#da7400',
  '#9747ff',
  '#bf3636',
  '#0891b2',
] as const;

export function signerColor(index: number): string {
  return SIGNER_COLORS[index % SIGNER_COLORS.length] as string;
}

/** A new signer, numbered after the ones already there. */
export function createSigner(existing: readonly TemplateSigner[]): TemplateSigner {
  const orderNo = existing.length + 1;
  return {
    orderNo,
    userId: crypto.randomUUID(),
    Name: `Signer ${orderNo}`,
    signType: DEFAULT_SIGN_TYPE,
    pDFPages: [],
  };
}

/**
 * Renumbers signers so `orderNo` stays 1..n with no gaps.
 *
 * The order is the SIGNING order — the server walks it to decide who is invited
 * next — so a gap left by a removed signer is not cosmetic.
 */
export function renumber(signers: readonly TemplateSigner[]): TemplateSigner[] {
  return signers.map((signer, index) => ({ ...signer, orderNo: index + 1 }));
}

export function signTypeLabel(value: string): string {
  return SIGN_TYPES.find((type) => type.value === value)?.label ?? 'Electronic';
}

export type { PdfPage };
